import {
  Alert,
  Typography,
  Button,
  SubSection,
  CurrencyInput,
  formatDate,
  Hint,
  Modal,
  Select,
  useLoading,
  ValueOpt,
} from "best-common-react";
import React, { useEffect, useRef, useState } from "react";
import {
  bulkPurchaseOpportunityTransaction,
  getLastChargeForPurchaseEntry,
  updatePurchaseOpportunityEntry,
} from "../../api/RequesTixApi";
import { PurchaseOpportunityStatusIds } from "../../constants/PurchaseOpportunityConstants";
import { AdminPurchaseOpportunityEntryDTO, PurchaseOpportunityStatus } from "../../types/PurchaseOpportunity";
import { TransactionHistory } from "../../types/Transaction";
import CreditCardDisplay from "../payment/CreditCardDisplay";
import { useDropdowns } from "../../contexts/DropdownsContext";
import { queries } from "../../queries";
import { useQueryClient } from "@tanstack/react-query";
type AdminPurchaseOpportunityGameFormProps = {
  entry: AdminPurchaseOpportunityEntryDTO;
  refresh: () => Promise<any>;
  formChanged: boolean;
};

type ChargeModalProps = {
  isCharge: boolean;
  entry: AdminPurchaseOpportunityEntryDTO;
  open: boolean;
  setOpen: (value: boolean) => void;
  refresh: () => Promise<any>;
};

export const ChargeModal: React.FC<ChargeModalProps> = ({ isCharge, entry, open, setOpen, refresh }) => {
  const { setLoading } = useLoading();
  const [amount, setAmount] = useState<number>(0);

  const [processing, setProcessing] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [error, setError] = useState<string>();
  const runningRef = useRef(false);

  useEffect(() => {
    if (open && !runningRef.current) {
      setHasSubmitted(false);
      setError(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!processing) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [processing]);

  const close = () => {
    if (!runningRef.current) setOpen(false);
  };

  const sendTransaction = async () => {
    if (runningRef.current || hasSubmitted || !Number.isFinite(amount) || amount <= 0) return;
    const userBillingId = entry.userBilling?.userBillingId;
    if (!userBillingId) {
      setError("A billing method is required.");
      return;
    }
    runningRef.current = true;
    setProcessing(true);
    setHasSubmitted(true);
    setError(undefined);
    let successful = false;
    const entryId = entry.purchaseOpportunityEntryId;
    try {
      const results = await bulkPurchaseOpportunityTransaction({
        purchaseOpportunities: [{ purchaseOpportunityEntryId: entryId, userBillingId }],
        amount,
        isCharge,
      });
      if (Array.isArray(results) && results.length === 0) {
        successful = true;
      } else if (
        Array.isArray(results) &&
        results.length === 1 &&
        results[0].purchaseOpportunityId === entryId &&
        typeof results[0].error === "string" &&
        results[0].error.trim() &&
        results[0].uncertain !== true
      ) {
        setError(results[0].error);
      } else {
        setError("Payment outcome is uncertain. Check the payment status before retrying.");
      }
    } catch {
      setError("Payment outcome is uncertain. Check the payment status before retrying.");
    } finally {
      try {
        await refresh();
      } catch {
        successful = false;
        setError((previous) =>
          previous
            ? `${previous} Refresh failed; reload the page to check the current status.`
            : "Payment confirmed, but refresh failed. Reload the page; do not submit this payment again.",
        );
      }
      runningRef.current = false;
      setProcessing(false);
      if (successful) setOpen(false);
    }
  };

  const fetchPreviousCharge = async (entryId: number) => {
    try {
      setLoading(true);
      const res: TransactionHistory = await getLastChargeForPurchaseEntry(entryId);
      setAmount(res.amount);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !isCharge) {
      void fetchPreviousCharge(entry.purchaseOpportunityEntryId);
    }
  }, [isCharge, entry, open]);

  return (
    <Modal show={open}>
      <Modal.Header close={processing ? undefined : close}>
        <Typography elementType="span" variant="titleMedium">
          {isCharge ? "Charge" : "Refund"} Card
        </Typography>
      </Modal.Header>
      <Modal.Body>
        {processing && (
          <Typography elementType="p" role="status">
            Processing payment and refreshing status. Please keep this page open.
          </Typography>
        )}
        {error && (
          <Alert type="danger">
            <Typography elementType="div">{error}</Typography>
          </Alert>
        )}
        <CurrencyInput
          id="chargeAmount"
          value={amount}
          onChange={(value: number) => setAmount(value)}
          label={isCharge ? "Charge Amount" : "Refund Amount"}
          disabled={!isCharge || processing || hasSubmitted}
          required
        />
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end">
          <Button
            className="bcr-me-2"
            variant="primary"
            onClick={sendTransaction}
            disabled={!Number.isFinite(amount) || amount <= 0 || processing || hasSubmitted}
          >
            {isCharge ? "Charge" : "Refund"}
          </Button>
          <Button onClick={close} disabled={processing}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const AdminPurchaseOpportunityGameForm: React.FC<AdminPurchaseOpportunityGameFormProps> = ({
  entry,
  refresh,
  formChanged,
}) => {
  const { setLoading } = useLoading();
  const { purchaseOpportunityStatusOptions } = useDropdowns();
  const [hasChanged, setHasChange] = useState<boolean>(false);
  const [localEntry, setLocalEntry] = useState<AdminPurchaseOpportunityEntryDTO>();
  const [chargeOpen, setChargeOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const changeStatus = (value: ValueOpt<PurchaseOpportunityStatus>) => {
    setHasChange(true);
    setLocalEntry({
      ...localEntry!,
      purchaseOpportunityStatus: value.value,
    });
  };

  const saveEntry = async () => {
    try {
      setLoading(true);
      await updatePurchaseOpportunityEntry(localEntry!);

      // Invalidate the specific purchase opportunity query
      await queryClient.invalidateQueries({
        queryKey: queries.purchaseOpportunity.getAdminPurchaseOpportunity(localEntry!.purchaseOpportunityId).queryKey,
      });

      // Also invalidate any broader purchase opportunity queries that might exist
      await queryClient.invalidateQueries({
        queryKey: queries.purchaseOpportunity._def,
      });

      await refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLocalEntry(entry);
  }, [entry]);

  return (
    <SubSection header={`Game ${entry.gameNumber}`} className="bcr-mb-2">
      {!!localEntry && (
        <>
          <ChargeModal
            isCharge={
              localEntry.purchaseOpportunityStatus.purchaseOpportunityStatusId === PurchaseOpportunityStatusIds.SELECTED
            }
            entry={entry}
            refresh={refresh}
            open={chargeOpen}
            setOpen={setChargeOpen}
          />
          <Select
            id={`${entry.purchaseOpportunityEntryId}-game-status`}
            options={purchaseOpportunityStatusOptions}
            value={purchaseOpportunityStatusOptions.find(
              (opt: ValueOpt<PurchaseOpportunityStatus>): boolean =>
                opt.value.purchaseOpportunityStatusId ===
                localEntry.purchaseOpportunityStatus.purchaseOpportunityStatusId,
            )}
            label="Entry Status"
            required
            onChange={changeStatus}
            gutterBottom
          />
          {[
            PurchaseOpportunityStatusIds.SELECTED,
            PurchaseOpportunityStatusIds.CHARGED,
            PurchaseOpportunityStatusIds.REFUNDED,
          ].includes(localEntry.purchaseOpportunityStatus.purchaseOpportunityStatusId) && (
            <div>
              <div className="d-flex">
                <CreditCardDisplay card={localEntry.userBilling} />
                {!!entry.lastChargedDate && (
                  <div className="bcr-ms-1">
                    <Hint>Charged on: {formatDate(new Date(entry.lastChargedDate))}</Hint>
                  </div>
                )}
              </div>

              <Button
                className="bcr-mt-1"
                variant="secondary"
                onClick={() => setChargeOpen(true)}
                disabled={formChanged}
              >
                {localEntry.purchaseOpportunityStatus.purchaseOpportunityStatusId ===
                PurchaseOpportunityStatusIds.CHARGED
                  ? "Refund"
                  : "Charge"}
                &nbsp;Card
              </Button>
            </div>
          )}
        </>
      )}
      <div className="bcr-mt-2">
        <Button variant="primary" disabled={!hasChanged} onClick={saveEntry}>
          Save
        </Button>
      </div>
    </SubSection>
  );
};

export default AdminPurchaseOpportunityGameForm;
